import React, { useCallback } from 'react'
import { Platform } from 'react-native'

import { WorldOutlined } from '@zet/agent-website/assets/svgs'
import {
  AgentWebsite,
  handleEnquiryTag,
  handleWhatsAppAndCallIcon,
} from '@zet/agent-website/src/utils/constants'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import { MY_TEAMS_ADVISOR } from '@zet/constants'
import { useAddCustomerNextState } from '@zet/customer/src/api/hooks'
import useCustomerStore from '@zet/customer/src/store'
import {
  B4,
  B5,
  Icon,
  SH3,
  Stack,
  XStack,
  colors as zetColors,
} from '@zet/design-system'
import Avatar from '@zet/design-system/avatar/Avatar'
import Pressable from '@zet/design-system/pressable'
import { APP_NAME } from '@zet/end-points'
import { useUserContext } from '@zet/feature-user'
import { i18n } from '@zet/localization'

import { IRequestData } from '../api/functions/customerAdditionStateMachine'
import { useVerifyPhoneNumber } from '../api/hooks'

type customerCardProps = {
  name?: string
  phone?: string
  eligible?: Array<string>
  subAgentName?: string
  preapprovedBrands?: Array<string>
  creditScoreLevel?: string
  isVerified?: boolean
  errorMessage?: string
  customerId?: number
  isBureauReportExpired?: boolean
  interestedEnquiryCategories?: string[]
  customerAdditionSource?: string
  isAgentWebsiteFlow?: boolean
  subAgentId?: number
}

const CustomerCard: React.FC<customerCardProps> = ({
  name,
  phone,
  eligible,
  subAgentName,
  preapprovedBrands,
  creditScoreLevel,
  isVerified = true,
  errorMessage,
  customerId,
  isBureauReportExpired: isBureauExpired,
  interestedEnquiryCategories,
  customerAdditionSource,
  isAgentWebsiteFlow = false,
  subAgentId,
  ...props
}) => {
  const isBureauReportExpired = isBureauExpired
  const navigation = useNavigation()
  const { data: userDetails } = useUserContext()

  const setSelectedCustomer = useCustomerStore(
    (state) => state.setSelectedCustomer
  )
  const { mutate } = useVerifyPhoneNumber()
  const { mutate: mutateWithExperiments } = useAddCustomerNextState()
  const handleOnPress = useCallback(() => {
    if (isVerified) {
      navigation.navigate('BrandPage', {
        customerId: customerId,
        customerName: name,
        customerPhoneNumber: phone,
        isBureauReportExpired: isBureauReportExpired,
        routeName: 'forCustomerFlow',
      })
    } else {
      setSelectedCustomer({
        name: name,
        phoneNumber: phone,
      })

      const requestBodyData = {
        fullName: name,
        phoneNumber: phone,
        screenName: 'SHOWN_PHONE_NUMBER_SCREEN',
        nonVerifiedBridgeCustomerAddition: true,
        nonVerfiedUser: true,
        ...(subAgentId !== 0 && { subagentId: subAgentId }),
      }

      mutateWithExperiments(requestBodyData as IRequestData)
    }
  }, [
    customerId,
    isBureauReportExpired,
    isVerified,
    mutate,
    name,
    navigation,
    phone,
    setSelectedCustomer,
  ])

  return (
    <Pressable
      opacity
      className="bg-neutral-white100 mt-1 justify-center py-1"
      onPress={handleOnPress}
      eventName="click_action"
      eventPayload={{
        item_name: 'add_customer_card',
        onecode: userDetails?.zetCode,
        mobile_number: userDetails?.phoneNumber,
        email_id: userDetails?.email,
        customer_name: name,
        customer_phonenumber: phone,
        credit_score: creditScoreLevel,
        high_gaurantee: preapprovedBrands ? 'yes' : 'No',
      }}
    >
      <XStack className="mt-4/2 mx-3 mb-2 items-center">
        <Avatar name={name} size="large" />
        <Stack className="ml-4/2 mb-2 w-9 flex-grow justify-start">
          <SH3 className="text-neutral-black100">{name}</SH3>
          <B4 className="text-grays-60">{phone}</B4>
        </Stack>
        {isVerified && !isAgentWebsiteFlow ? (
          <Stack>
            {creditScoreLevel &&
            creditScoreLevel !== 'NA' &&
            !isBureauReportExpired ? (
              <XStack
                className={`rounded-[3] ${
                  creditScoreLevel === 'Excellent'
                    ? 'bg-[#D4FFE6]'
                    : creditScoreLevel === 'High'
                    ? 'bg-[#EAF3FF]'
                    : creditScoreLevel === 'Medium'
                    ? 'bg-[#FFF5DE]'
                    : 'bg-[#FFEAEA]'
                } px-2 py-[2]`}
              >
                <Icon
                  type={`${
                    creditScoreLevel === 'Excellent'
                      ? 'CreditScoreExcellent'
                      : creditScoreLevel === 'High'
                      ? 'CreditScoreHigh'
                      : creditScoreLevel === 'Medium'
                      ? 'CreditScoreMedium'
                      : 'CreditScoreLow'
                  }`}
                  size={20}
                />
                <B4 className="ml-[2]">{creditScoreLevel}</B4>
              </XStack>
            ) : isBureauReportExpired ? (
              <Stack className="rounded-[3] bg-[#FFE6E0]  px-2 py-[2]">
                <B4>{i18n.t('myCustomers.scoreExpired')}</B4>
              </Stack>
            ) : (
              <Stack className="bg-grays-30  rounded-[3] px-2 py-[2]">
                <B4>{i18n.t('myCustomers.newCredit')}</B4>
              </Stack>
            )}
          </Stack>
        ) : isAgentWebsiteFlow ? (
          <XStack className="items-center">
            {handleWhatsAppAndCallIcon(phone)?.map((item, index) => (
              <Pressable
                className={`border ${item?.borderColor} rounded p-1 ${
                  index === 1 ? 'ml-2' : ''
                }`}
                key={index}
                onPress={item?.onPress}
              >
                {item?.icon}
              </Pressable>
            ))}
          </XStack>
        ) : (
          <></>
        )}
      </XStack>
      {APP_NAME !== MY_TEAMS_ADVISOR &&
        customerAdditionSource &&
        customerAdditionSource === AgentWebsite.AGENT_WEBSITE && (
          <XStack className="bg-grays-20 mx-3 -mt-2 mb-2 items-center rounded px-1 py-1">
            <WorldOutlined
              height={16}
              width={16}
              color={zetColors.primary[150]}
            />
            <B5 className="text-grays-60 ml-2">{i18n.t('addedFromWeb')}</B5>
          </XStack>
        )}
      {APP_NAME !== MY_TEAMS_ADVISOR &&
        interestedEnquiryCategories &&
        interestedEnquiryCategories[0] && (
          <XStack className="mx-3 mb-2 items-center pl-1">
            {handleEnquiryTag(interestedEnquiryCategories[0])?.icon}
            <B5 className="text-grays-60 ml-2">
              {handleEnquiryTag(interestedEnquiryCategories[0])?.text}
            </B5>
          </XStack>
        )}
      {APP_NAME !== MY_TEAMS_ADVISOR && subAgentName ? (
        <Stack className="bg-grays-20 mx-3 mb-2 h-4 rounded-sm p-1">
          <B5 className="text-grays-60">{`Added by ${subAgentName}`}</B5>
        </Stack>
      ) : null}
      {Platform.OS !== 'web' && (
        <Stack>
          {eligible?.length > 0 ? (
            eligible?.length === 1 ? (
              <B5 className="text-grays-60 mx-3   mb-2">{`Eligible for only ${eligible[0]}`}</B5>
            ) : eligible?.length === 2 ? (
              <B5 className="text-grays-60 mx-3   mb-2">{`Eligible for ${eligible[0]} & ${eligible[1]}`}</B5>
            ) : (
              <B5 className=" text-grays-60 mx-3  mb-2">{`Eligible for ${
                eligible[0]
              }, ${eligible[1]} & ${eligible.length - 2} more`}</B5>
            )
          ) : null}
        </Stack>
      )}

      {preapprovedBrands ? (
        preapprovedBrands?.length === 1 ? (
          <XStack className="my-4/2 mx-3 items-center rounded bg-[#FFF1E3] px-1">
            <Icon type={'OfferOutlined'} size={20} className="mt-1" />
            <B5 className="text-grays-60">{`Pre-approved card for only ${preapprovedBrands[0]}`}</B5>
          </XStack>
        ) : preapprovedBrands?.length === 2 ? (
          <XStack className="my-4/2 mx-3 items-center rounded bg-[#FFF1E3] px-1">
            <Icon type={'OfferOutlined'} size={20} className="mt-1" />
            <B5 className="text-grays-60">{`Pre-approved card for ${preapprovedBrands[0]} & ${preapprovedBrands[1]}`}</B5>
          </XStack>
        ) : (
          <XStack className="my-4/2 mx-3 items-center rounded bg-[#FFF1E3] px-1">
            <Icon type={'OfferOutlined'} size={20} className="mt-1" />
            <B5 className="text-grays-60">{`Pre-approved card for ${
              preapprovedBrands[0]
            }, ${preapprovedBrands[1]} & ${
              preapprovedBrands.length - 2
            } more`}</B5>
          </XStack>
        )
      ) : null}

      {!isVerified && errorMessage && (
        <XStack className="mx-3 mb-2.5  items-center rounded-[4] bg-[#FFECEC] px-1 py-1">
          <Icon type={'ErrorOutlined'} size={16} color={zetColors.error[150]} />
          <B5 className="text-grays-60 ml-2">{errorMessage}</B5>
        </XStack>
      )}
      {isBureauReportExpired && (
        <XStack className="mb-4/2 mx-3 items-center rounded bg-[#FFF1E3] px-2 py-1">
          <Icon type="CautionOutlined" color={zetColors.grays[80]} size={14} />
          <B5 className="text-grays-60 ml-1">
            {i18n.t('reconsent.subHeading')}
          </B5>
        </XStack>
      )}
    </Pressable>
  )
}
export default CustomerCard
